import {
  Children,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from "react";
import type { Span } from "./shape";
import { Goo } from "./goo";

/**
 * Полоса капель из макета v6 — поле ввода. Капли стоят там, куда их ставит
 * `spans`, и перетекают пружинами; соседи ближе `merge` сливаются в одну.
 * Материал у капель — CSS снаружи (`composer-goo`); стекла движка нет.
 *
 * Детей ровно столько же, сколько отрезков в `spans`: каждый ребёнок — начинка
 * своей капли. Коробка полосы берёт размер у CSS (класс снаружи), капли
 * раскладываются по её ширине — `spans` получает её числом.
 *
 * Мимо капель мышь проходит насквозь: коробка шире того, что в ней нарисовано,
 * и зазор между каплями не должен отбирать у ленты прокрутку и нажатия.
 */
export function GooBar({
  ref,
  className = "",
  style,
  height,
  spans,
  merge = 6,
  damping = 23,
  radius = null,
  from,
  morph,
  children,
}: {
  ref?: Ref<HTMLDivElement>;
  className?: string;
  style?: CSSProperties;
  /** Высота капель. */
  height: number;
  /** Края капель по ширине коробки. */
  spans: (width: number) => Span[];
  merge?: number;
  damping?: number;
  /** Скруглённый прямоугольник вместо капель (слитое многострочное поле). */
  radius?: number | null;
  /** Откуда капли выходят при первом показе — по умолчанию сразу стоят на местах. */
  from?: (width: number) => Span[];
  /** Сменился — по каплям проходит короткое размытие, как в макете при слиянии. */
  morph?: unknown;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const clip = useRef<HTMLDivElement>(null);
  const goo = useRef<Goo | null>(null);
  const [width, setWidth] = useState(0);

  // Раскладка читается при каждом кадре цели, а не при монтаже: она функция от ширины.
  const latest = useRef({ height, spans, merge, damping, radius, from });
  latest.current = { height, spans, merge, damping, radius, from };

  useLayoutEffect(() => {
    const host = box.current!;
    const w = host.offsetWidth;
    const start = latest.current.from?.(w);
    goo.current = new Goo(host, clip.current!, start);
    setWidth(w);

    const ro = new ResizeObserver(() => {
      setWidth(host.offsetWidth);
      goo.current?.kick();
    });
    ro.observe(host);

    return () => {
      ro.disconnect();
      goo.current?.destroy();
      goo.current = null;
    };
  }, []);

  // Цель — на каждую отрисовку: высота, раскладка и слияние зависят от состояния снаружи.
  useLayoutEffect(() => {
    const now = latest.current;
    if (!goo.current || !width) return;
    goo.current.aim({
      h: now.height,
      spans: now.spans(width),
      merge: now.merge,
      damping: now.damping,
      radius: now.radius,
    });
  });

  // Короткое размытие при смене формы — макет так прячет перестройку начинки.
  const seen = useRef(morph);
  useEffect(() => {
    if (seen.current === morph) return;
    seen.current = morph;
    const c = clip.current;
    if (!c || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    c.getAnimations().forEach((a) => a.cancel());
    c.animate([{ filter: "blur(0px)" }, { filter: "blur(5px)", offset: 0.3 }, { filter: "blur(0px)" }], {
      duration: 360,
      easing: "ease-out",
    });
  }, [morph]);

  const setBox = (node: HTMLDivElement | null) => {
    box.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  return (
    <div ref={setBox} className={`goo ${className}`} style={style}>
      <div ref={clip} className="goo-clip">
        {Children.toArray(children).map((child, index) => (
          <div key={index} className="goo-item" data-goo-item={index}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}
